import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { theme, alpha } from '../../../theme';
import { useResponsive } from '../../../hooks/useResponsive';
import { ChevronRight, Check, Building2, MapPin, Clock } from 'lucide-react';

interface OnboardingStep {
  id: number;
  title: string;
  description: string;
  completed: boolean;
}

const OnboardingWizard: React.FC = () => {
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);

  // Step 2: Complex Form
  const [complexName, setComplexName] = useState('');
  const [complexAddress, setComplexAddress] = useState('');
  const [complexCity, setComplexCity] = useState('');
  const [complexProvince, setComplexProvince] = useState('Buenos Aires');
  const [hasParking, setHasParking] = useState(false);
  const [hasLocker, setHasLocker] = useState(false);
  const [hasCafeteria, setHasCafeteria] = useState(false);

  // Step 3: Venue Form
  const [venueName, setVenueName] = useState('Cancha 1');
  const [venueType, setVenueType] = useState<'FUTBOL_5' | 'FUTBOL_7' | 'FUTBOL_11'>('FUTBOL_5');
  const [venuePrice, setVenuePrice] = useState('');
  const [isIndoor, setIsIndoor] = useState(false);

  const steps: OnboardingStep[] = [
    {
      id: 0,
      title: 'Bienvenido a Futbolero Canchas',
      description: 'Configurá tu complejo en 4 pasos simples',
      completed: currentStep > 0
    },
    {
      id: 1,
      title: 'Creá tu Complejo',
      description: 'Información básica de tu complejo deportivo',
      completed: currentStep > 1
    },
    {
      id: 2,
      title: 'Agregá tu Primera Cancha',
      description: 'Configurá los detalles de tu cancha',
      completed: currentStep > 2
    },
    {
      id: 3,
      title: 'Configurá Disponibilidad',
      description: 'Define tus horarios de atención',
      completed: currentStep > 3
    }
  ];

  // Load from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('futbolero_onboarding_progress');
    if (saved) {
      try {
        const data = JSON.parse(saved);
        setCurrentStep(data.currentStep || 0);
        setComplexName(data.complexName || '');
        setComplexAddress(data.complexAddress || '');
        setComplexCity(data.complexCity || '');
        setComplexProvince(data.complexProvince || 'Buenos Aires');
        setHasParking(data.hasParking || false);
        setHasLocker(data.hasLocker || false);
        setHasCafeteria(data.hasCafeteria || false);
        setVenueName(data.venueName || 'Cancha 1');
        setVenueType(data.venueType || 'FUTBOL_5');
        setVenuePrice(data.venuePrice || '');
        setIsIndoor(data.isIndoor || false);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Error loading onboarding progress:', e);
      }
    }
  }, []);

  // Save to localStorage on every change
  useEffect(() => {
    const data = {
      currentStep,
      complexName,
      complexAddress,
      complexCity,
      complexProvince,
      hasParking,
      hasLocker,
      hasCafeteria,
      venueName,
      venueType,
      venuePrice,
      isIndoor
    };
    localStorage.setItem('futbolero_onboarding_progress', JSON.stringify(data));
  }, [currentStep, complexName, complexAddress, complexCity, complexProvince, hasParking, hasLocker, hasCafeteria, venueName, venueType, venuePrice, isIndoor]);

  const handleNext = async () => {
    if (currentStep === 0) {
      // Welcome step - just advance
      setCurrentStep(1);
    } else if (currentStep === 1) {
      // Create Complex
      if (!complexName.trim() || !complexAddress.trim() || !complexCity.trim()) {
        alert('Por favor completá todos los campos requeridos');
        return;
      }

      setLoading(true);
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/complexes`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            name: complexName,
            address: complexAddress,
            city: complexCity,
            province: complexProvince,
            hasParking,
            hasChangingRooms: hasLocker,
            hasBuffet: hasCafeteria
          })
        });

        if (!response.ok) {
          const error = await response.json();
          // If complex already exists, use that one and continue
          if (response.status === 400 && error.complexId) {
            localStorage.setItem('futbolero_complex_id', error.complexId);
            setCurrentStep(2);
            return;
          }
          throw new Error(error.error || 'Error al crear el complejo');
        }

        const data = await response.json();
        localStorage.setItem('futbolero_complex_id', data.complex?.id || data.id);
        setCurrentStep(2);
      } catch (error: any) {
        alert(error.message || 'Error al crear el complejo');
      } finally {
        setLoading(false);
      }
    } else if (currentStep === 2) {
      // Create Venue
      if (!venueName.trim() || !venuePrice) {
        alert('Por favor completá todos los campos requeridos');
        return;
      }

      setLoading(true);
      try {
        const token = localStorage.getItem('authToken');
        const complexId = localStorage.getItem('futbolero_complex_id');

        if (!complexId) {
          throw new Error('No se encontró el ID del complejo');
        }

        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/venues`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            name: venueName,
            venueType,
            hourlyRate: parseFloat(venuePrice),
            isIndoor
          })
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || 'Error al crear la cancha');
        }

        const venue = await response.json();
        localStorage.setItem('futbolero_venue_id', venue.id);
        setCurrentStep(3);
      } catch (error: any) {
        alert(error.message || 'Error al crear la cancha');
      } finally {
        setLoading(false);
      }
    } else if (currentStep === 3) {
      // Create default availability (Mon-Sun 8:00-23:00)
      const venueId = localStorage.getItem('futbolero_venue_id');
      if (venueId) {
        setLoading(true);
        try {
          const token = localStorage.getItem('authToken');
          const defaultAvailability = [0, 1, 2, 3, 4, 5, 6].map((day) => ({
            dayOfWeek: day,
            startTime: '08:00',
            endTime: '23:00',
            isPeakHour: day === 0 || day === 6, // weekends are peak
          }));

          await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/venues/${venueId}/availability`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ availability: defaultAvailability })
          });
        } catch (err) {
          // Non-critical: availability can be configured later
          // eslint-disable-next-line no-console
          console.warn('Could not set default availability:', err);
        } finally {
          setLoading(false);
        }
      }
      localStorage.removeItem('futbolero_onboarding_progress');
      navigate('/owner/reservations?setup=complete');
    }
  };

  const handleSkip = () => {
    if (window.confirm('¿Estás seguro que querés saltar el onboarding? Podés configurar tu complejo después desde el dashboard.')) {
      localStorage.removeItem('futbolero_onboarding_progress');
      navigate('/owner/dashboard');
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        // Welcome
        return (
          <div style={{ textAlign: 'center', maxWidth: '600px', margin: '0 auto' }}>
            <div style={{ fontSize: '80px', marginBottom: '24px' }}>🏟️</div>
            <h2 style={{
              fontSize: isMobile ? '28px' : '36px',
              fontWeight: 700,
              color: theme.colors.text,
              margin: '0 0 16px 0',
              fontFamily: theme.fonts.heading
            }}>
              ¡Bienvenido a Futbolero Canchas!
            </h2>
            <p style={{
              fontSize: isMobile ? '16px' : '18px',
              color: theme.colors.textSecondary,
              margin: '0 0 32px 0',
              lineHeight: 1.6
            }}>
              Vamos a configurar tu complejo en 4 pasos simples. Tomará solo unos minutos y después podrás empezar a recibir reservas automáticas.
            </p>

            <div style={{
              backgroundColor: theme.colors.surface,
              borderRadius: '12px',
              padding: '24px',
              textAlign: 'left',
              marginBottom: '32px'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: 600,
                color: theme.colors.text,
                margin: '0 0 16px 0'
              }}>
                Lo que vas a configurar:
              </h3>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0
              }}>
                {[
                  'Información de tu complejo deportivo',
                  'Tu primera cancha con precios',
                  'Horarios de disponibilidad',
                  'Integración con Mercado Pago (opcional)'
                ].map((item, i) => (
                  <li key={i} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '12px',
                    color: theme.colors.textSecondary,
                    fontSize: '15px'
                  }}>
                    <Check size={20} color={theme.colors.primary} />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        );

      case 1:
        // Create Complex
        return (
          <div style={{ maxWidth: '600px', margin: '0 auto' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '24px'
            }}>
              <Building2 size={32} color={theme.colors.primary} />
              <h2 style={{
                fontSize: isMobile ? '24px' : '28px',
                fontWeight: 700,
                color: theme.colors.text,
                margin: 0,
                fontFamily: theme.fonts.heading
              }}>
                Creá tu Complejo Deportivo
              </h2>
            </div>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '20px'
            }}>
              {/* Complex Name */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: theme.colors.text,
                  marginBottom: '8px'
                }}>
                  Nombre del Complejo *
                </label>
                <input
                  data-testid="complex-name-input"
                  type="text"
                  value={complexName}
                  onChange={(e) => setComplexName(e.target.value)}
                  placeholder="Ej: Complejo Deportivo San Martín"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    backgroundColor: theme.colors.surface,
                    border: `1px solid ${theme.colors.border}`,
                    borderRadius: '8px',
                    color: theme.colors.text,
                    fontSize: '15px',
                    fontFamily: theme.fonts.body
                  }}
                />
              </div>

              {/* Address */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: theme.colors.text,
                  marginBottom: '8px'
                }}>
                  Dirección *
                </label>
                <input
                  data-testid="complex-address-input"
                  type="text"
                  value={complexAddress}
                  onChange={(e) => setComplexAddress(e.target.value)}
                  placeholder="Ej: Av. San Martín 1234"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    backgroundColor: theme.colors.surface,
                    border: `1px solid ${theme.colors.border}`,
                    borderRadius: '8px',
                    color: theme.colors.text,
                    fontSize: '15px',
                    fontFamily: theme.fonts.body
                  }}
                />
              </div>

              {/* City & Province */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                gap: '16px'
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: theme.colors.text,
                    marginBottom: '8px'
                  }}>
                    Ciudad *
                  </label>
                  <input
                    data-testid="complex-city-input"
                    type="text"
                    value={complexCity}
                    onChange={(e) => setComplexCity(e.target.value)}
                    placeholder="Ej: Buenos Aires"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      backgroundColor: theme.colors.surface,
                      border: `1px solid ${theme.colors.border}`,
                      borderRadius: '8px',
                      color: theme.colors.text,
                      fontSize: '15px',
                      fontFamily: theme.fonts.body
                    }}
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: theme.colors.text,
                    marginBottom: '8px'
                  }}>
                    Provincia *
                  </label>
                  <select
                    value={complexProvince}
                    onChange={(e) => setComplexProvince(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      backgroundColor: theme.colors.surface,
                      border: `1px solid ${theme.colors.border}`,
                      borderRadius: '8px',
                      color: theme.colors.text,
                      fontSize: '15px',
                      fontFamily: theme.fonts.body
                    }}
                  >
                    <option value="Buenos Aires">Buenos Aires</option>
                    <option value="CABA">CABA</option>
                    <option value="Córdoba">Córdoba</option>
                    <option value="Santa Fe">Santa Fe</option>
                    <option value="Mendoza">Mendoza</option>
                  </select>
                </div>
              </div>

              {/* Amenities */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: theme.colors.text,
                  marginBottom: '12px'
                }}>
                  Comodidades
                </label>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px'
                }}>
                  {[
                    { label: 'Estacionamiento', value: hasParking, setter: setHasParking },
                    { label: 'Vestuarios', value: hasLocker, setter: setHasLocker },
                    { label: 'Cafetería', value: hasCafeteria, setter: setHasCafeteria }
                  ].map((item, i) => (
                    <label key={i} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      cursor: 'pointer',
                      padding: '12px',
                      backgroundColor: item.value ? `${alpha('primary', '10')}` : theme.colors.surface,
                      borderRadius: '8px',
                      border: `1px solid ${item.value ? theme.colors.primary : theme.colors.border}`,
                      transition: 'all 0.2s ease'
                    }}>
                      <input
                        type="checkbox"
                        checked={item.value}
                        onChange={(e) => item.setter(e.target.checked)}
                        style={{
                          width: '20px',
                          height: '20px',
                          cursor: 'pointer'
                        }}
                      />
                      <span style={{
                        fontSize: '15px',
                        color: theme.colors.text,
                        fontWeight: 500
                      }}>
                        {item.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 2:
        // Add Venue
        return (
          <div style={{ maxWidth: '600px', margin: '0 auto' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '24px'
            }}>
              <MapPin size={32} color={theme.colors.primary} />
              <h2 style={{
                fontSize: isMobile ? '24px' : '28px',
                fontWeight: 700,
                color: theme.colors.text,
                margin: 0,
                fontFamily: theme.fonts.heading
              }}>
                Agregá tu Primera Cancha
              </h2>
            </div>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '20px'
            }}>
              {/* Venue Name */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: theme.colors.text,
                  marginBottom: '8px'
                }}>
                  Nombre de la Cancha *
                </label>
                <input
                  type="text"
                  value={venueName}
                  onChange={(e) => setVenueName(e.target.value)}
                  placeholder="Ej: Cancha 1, Cancha Principal"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    backgroundColor: theme.colors.surface,
                    border: `1px solid ${theme.colors.border}`,
                    borderRadius: '8px',
                    color: theme.colors.text,
                    fontSize: '15px',
                    fontFamily: theme.fonts.body
                  }}
                />
              </div>

              {/* Venue Type */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: theme.colors.text,
                  marginBottom: '12px'
                }}>
                  Tipo de Cancha *
                </label>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                  gap: '12px'
                }}>
                  {[
                    { value: 'FUTBOL_5', label: 'Fútbol 5' },
                    { value: 'FUTBOL_7', label: 'Fútbol 7' },
                    { value: 'FUTBOL_11', label: 'Fútbol 11' }
                  ].map((type) => (
                    <button
                      key={type.value}
                      data-testid={`venue-type-${type.value}`}
                      onClick={() => setVenueType(type.value as any)}
                      style={{
                        padding: '16px',
                        backgroundColor: venueType === type.value ? theme.colors.primary : theme.colors.surface,
                        color: venueType === type.value ? '#000000' : theme.colors.text,
                        border: `2px solid ${venueType === type.value ? theme.colors.primary : theme.colors.border}`,
                        borderRadius: '8px',
                        fontSize: '15px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        fontFamily: theme.fonts.body,
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Price */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: theme.colors.text,
                  marginBottom: '8px'
                }}>
                  Precio por Hora *
                </label>
                <div style={{ position: 'relative' }}>
                  <span style={{
                    position: 'absolute',
                    left: '16px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: theme.colors.textSecondary,
                    fontSize: '15px',
                    fontWeight: 600
                  }}>
                    $
                  </span>
                  <input
                    data-testid="venue-price-input"
                    type="number"
                    value={venuePrice}
                    onChange={(e) => setVenuePrice(e.target.value)}
                    placeholder="10000"
                    style={{
                      width: '100%',
                      padding: '12px 16px 12px 32px',
                      backgroundColor: theme.colors.surface,
                      border: `1px solid ${theme.colors.border}`,
                      borderRadius: '8px',
                      color: theme.colors.text,
                      fontSize: '15px',
                      fontFamily: theme.fonts.body
                    }}
                  />
                </div>
              </div>

              {/* Indoor/Outdoor */}
              <div>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  cursor: 'pointer',
                  padding: '16px',
                  backgroundColor: isIndoor ? `${alpha('primary', '10')}` : theme.colors.surface,
                  borderRadius: '8px',
                  border: `1px solid ${isIndoor ? theme.colors.primary : theme.colors.border}`,
                  transition: 'all 0.2s ease'
                }}>
                  <input
                    type="checkbox"
                    checked={isIndoor}
                    onChange={(e) => setIsIndoor(e.target.checked)}
                    style={{
                      width: '20px',
                      height: '20px',
                      cursor: 'pointer'
                    }}
                  />
                  <div>
                    <div style={{
                      fontSize: '15px',
                      color: theme.colors.text,
                      fontWeight: 600,
                      marginBottom: '4px'
                    }}>
                      Cancha Techada
                    </div>
                    <div style={{
                      fontSize: '13px',
                      color: theme.colors.textSecondary
                    }}>
                      Marcá si tu cancha está bajo techo
                    </div>
                  </div>
                </label>
              </div>
            </div>
          </div>
        );

      case 3:
        // Availability
        return (
          <div style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              backgroundColor: `${alpha('primary', '20')}`,
              marginBottom: '24px'
            }}>
              <Clock size={40} color={theme.colors.primary} />
            </div>

            <h2 style={{
              fontSize: isMobile ? '24px' : '28px',
              fontWeight: 700,
              color: theme.colors.text,
              margin: '0 0 16px 0',
              fontFamily: theme.fonts.heading
            }}>
              Configurá tu Disponibilidad
            </h2>

            <p style={{
              fontSize: isMobile ? '16px' : '18px',
              color: theme.colors.textSecondary,
              margin: '0 0 32px 0',
              lineHeight: 1.6
            }}>
              Por ahora dejamos configurados horarios por defecto de 8:00 a 23:00, todos los días. Podés personalizarlos después desde el dashboard.
            </p>

            <div style={{
              backgroundColor: theme.colors.surface,
              borderRadius: '12px',
              padding: '24px',
              textAlign: 'left',
              marginBottom: '32px'
            }}>
              <h3 style={{
                fontSize: '16px',
                fontWeight: 600,
                color: theme.colors.text,
                margin: '0 0 16px 0'
              }}>
                Horarios por defecto:
              </h3>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                {[
                  'Lunes a Domingo: 8:00 - 23:00',
                  'Turnos de 1 hora',
                  'Reservas con hasta 30 días de anticipación'
                ].map((item, i) => (
                  <div key={i} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    color: theme.colors.textSecondary,
                    fontSize: '15px'
                  }}>
                    <Check size={20} color={theme.colors.primary} />
                    {item}
                  </div>
                ))}
              </div>
            </div>

            <div style={{
              backgroundColor: `${alpha('primary', '10')}`,
              borderRadius: '12px',
              padding: '20px',
              border: `1px solid ${alpha('primary', '40')}`
            }}>
              <p style={{
                fontSize: '14px',
                color: theme.colors.text,
                margin: 0,
                lineHeight: 1.6
              }}>
                💡 <strong>Próximo paso:</strong> Conectá tu cuenta de Mercado Pago desde el dashboard para empezar a recibir pagos automáticos.
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div style={{
      backgroundColor: theme.colors.background,
      minHeight: '100vh',
      padding: isMobile ? '24px' : '40px 20px'
    }}>
      <div style={{
        maxWidth: '900px',
        margin: '0 auto'
      }}>
        {/* Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '48px'
        }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '16px'
          }}>
            <div style={{
              width: '36px',
              height: '36px',
              background: theme.colors.primary,
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transform: 'rotate(-5deg)'
            }}>
              <span style={{ fontSize: '20px' }}>⚽</span>
            </div>
            <span style={{
              fontSize: '24px',
              fontWeight: 700,
              color: theme.colors.primary,
              fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
              letterSpacing: '1px'
            }}>
              FUTBOLERO
            </span>
          </div>
        </div>

        {/* Progress Bar */}
        <div style={{
          marginBottom: '48px'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px'
          }}>
            {steps.map((step, index) => (
              <div
                key={step.id}
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  flex: 1
                }}>
                  <div style={{
                    width: isMobile ? '32px' : '40px',
                    height: isMobile ? '32px' : '40px',
                    borderRadius: '50%',
                    backgroundColor: currentStep >= index ? theme.colors.primary : theme.colors.surface,
                    border: `2px solid ${currentStep >= index ? theme.colors.primary : theme.colors.border}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: isMobile ? '14px' : '16px',
                    color: currentStep >= index ? '#000000' : theme.colors.textSecondary,
                    transition: 'all 0.3s ease',
                    marginBottom: '8px'
                  }}>
                    {step.completed ? <Check size={isMobile ? 16 : 20} /> : index + 1}
                  </div>
                  {!isMobile && (
                    <span style={{
                      fontSize: '12px',
                      color: currentStep >= index ? theme.colors.text : theme.colors.textSecondary,
                      fontWeight: currentStep === index ? 600 : 400,
                      textAlign: 'center',
                      maxWidth: '120px'
                    }}>
                      {step.title.split(' ').slice(0, 2).join(' ')}
                    </span>
                  )}
                </div>
                {index < steps.length - 1 && (
                  <div style={{
                    flex: 1,
                    height: '2px',
                    backgroundColor: currentStep > index ? theme.colors.primary : theme.colors.border,
                    margin: isMobile ? '0 4px 24px 4px' : '0 8px 24px 8px',
                    transition: 'all 0.3s ease'
                  }} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div style={{
          backgroundColor: theme.colors.surface,
          borderRadius: '16px',
          padding: isMobile ? '24px' : '40px',
          marginBottom: '32px',
          border: `1px solid ${theme.colors.border}`,
          minHeight: '400px'
        }}>
          {renderStepContent()}
        </div>

        {/* Navigation Buttons */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '16px'
        }}>
          <button
            onClick={handleSkip}
            disabled={loading}
            style={{
              padding: '12px 24px',
              backgroundColor: 'transparent',
              color: theme.colors.textSecondary,
              border: 'none',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
              fontFamily: theme.fonts.body,
              opacity: loading ? 0.5 : 1,
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.currentTarget.style.color = theme.colors.text;
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.currentTarget.style.color = theme.colors.textSecondary;
              }
            }}
          >
            Saltar onboarding
          </button>

          <div style={{
            display: 'flex',
            gap: '12px'
          }}>
            {currentStep > 0 && (
              <button
                onClick={() => setCurrentStep(currentStep - 1)}
                disabled={loading}
                style={{
                  padding: '14px 28px',
                  backgroundColor: theme.colors.surface,
                  color: theme.colors.text,
                  border: `2px solid ${theme.colors.border}`,
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: 600,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontFamily: theme.fonts.body,
                  opacity: loading ? 0.5 : 1,
                  transition: 'all 0.2s ease'
                }}
              >
                Atrás
              </button>
            )}

            <button
              data-testid="onboarding-next-btn"
              onClick={handleNext}
              disabled={loading}
              style={{
                padding: '14px 28px',
                backgroundColor: theme.colors.primary,
                color: '#000000',
                border: 'none',
                borderRadius: '8px',
                fontSize: '15px',
                fontWeight: 700,
                cursor: loading ? 'not-allowed' : 'pointer',
                fontFamily: theme.fonts.body,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                opacity: loading ? 0.6 : 1,
                transition: 'all 0.2s ease',
                boxShadow: `0 4px 16px ${alpha('primary', '40')}`
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = `0 8px 24px ${alpha('primary', '60')}`;
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = `0 4px 16px ${alpha('primary', '40')}`;
                }
              }}
            >
              {loading ? 'Guardando...' : currentStep === 3 ? 'Ir al Dashboard' : 'Continuar'}
              {!loading && <ChevronRight size={20} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;
